import React, {
  useState, useRef, useEffect, useImperativeHandle, forwardRef,
} from "react";
import { Input } from "@/components/ui/input";

/**
 * <OtpInput length={6} onComplete={(code) => …} testidPrefix="…" />
 *
 * Self-contained N-digit OTP input row.
 *
 * - Auto-advances focus on entry, retreats on Backspace, supports paste of full code
 * - Calls `onComplete(code)` exactly once when all N digits are filled
 *   (uses a ref guard so React 18 strict-mode double-renders won't fire twice)
 * - Parent can call `ref.current.reset()` to clear all digits and refocus index 0
 *
 * `data-testid` IDs emitted: `${testidPrefix}-row`, `${testidPrefix}-digit-${0..N-1}`
 */
export const OtpInput = forwardRef(function OtpInput(
  { length = 6, autoFocus = true, onComplete, onChange, testidPrefix = "otp", className = "" },
  ref
) {
  const [digits, setDigits] = useState(() => Array(length).fill(""));
  const inputs = useRef([]);
  const completedRef = useRef(false);

  const code = digits.join("");
  const ready = code.length === length && !digits.includes("");

  useImperativeHandle(ref, () => ({
    reset: () => {
      setDigits(Array(length).fill(""));
      completedRef.current = false;
      inputs.current[0]?.focus();
    },
  }), [length]);

  useEffect(() => { if (autoFocus) inputs.current[0]?.focus(); }, [autoFocus]);

  // Keep parent in sync with the current value on every change
  useEffect(() => { onChange?.(code); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ready && !completedRef.current) {
      completedRef.current = true;
      onComplete?.(code);
    }
    if (!ready) completedRef.current = false;
  }, [ready, code]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    setDigits((p) => { const n = [...p]; n[i] = clean; return n; });
    if (clean && i < length - 1) inputs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted.length >= 1) {
      e.preventDefault();
      const padded = pasted.padEnd(length, "").split("").slice(0, length);
      setDigits(padded);
      const focusIdx = Math.min(pasted.length, length - 1);
      inputs.current[focusIdx]?.focus();
    }
  };

  return (
    <div className={`flex justify-between gap-2 ${className}`} data-testid={`${testidPrefix}-row`}>
      {digits.map((d, i) => (
        <Input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          value={d}
          onChange={(e) => setDigit(i, e.target.value)}
          onPaste={i === 0 ? onPaste : undefined}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !d && i > 0) inputs.current[i - 1]?.focus();
          }}
          inputMode="numeric"
          maxLength={1}
          autoComplete="one-time-code"
          className="h-14 w-12 sm:w-14 text-center font-brand text-[28px] font-bold tabular-nums"
          data-testid={`${testidPrefix}-digit-${i}`}
        />
      ))}
    </div>
  );
});
